import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LiveChatUser } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { isMobile } from "@scrile/tools/dist/lib/browserUtils";
import useBreakPoints from "hooks/useBreakPoints";
import useMounted from "hooks/useMounted";
import { useUserFirstDocInteract } from "hooks/useUserFirstDocInteract";
import { RightPanelView } from "./components/RightPanel";

interface Props {
  userList: LiveChatUser[];
  onClose: () => void;
  showConfirmationExit: boolean;
  hideChatOnStart?: boolean;
  chatInLive?: boolean;
}

interface DisplayControls {
  rightPanel: RightPanelView;
  rightPanelHidden: boolean;
  controls: boolean;
  confirmationExit: boolean;
  hasUnreadMessage: boolean;
  chatWasHidden: boolean;
  fullscreen: boolean;
}

function useViewController({ userList, onClose, showConfirmationExit, hideChatOnStart, chatInLive }: Props) {
  const usersCount = userList.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");

  const { showStreamPlayButton } = useUserFirstDocInteract();
  const { desktopUp, mobileLandscape } = useBreakPoints();

  const [displayControls, setDisplayControls] = useState<DisplayControls>({
    rightPanel: RightPanelView.CHAT,
    rightPanelHidden: hideChatOnStart ?? (!chatInLive && !desktopUp),
    controls: false,
    confirmationExit: false,
    hasUnreadMessage: false,
    chatWasHidden: hideChatOnStart ?? (!chatInLive && !desktopUp),
    fullscreen: false,
  });

  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMounted = useMounted();

  const showControlsButtons = useMemo(() => {
    if (!isMobile()) {
      return displayControls.controls || desktopUp;
    } else {
      return (
        (displayControls.controls || desktopUp) &&
        (chatInLive ? displayControls.rightPanel === RightPanelView.CHAT : displayControls.rightPanelHidden)
      );
    }
  }, [displayControls, desktopUp, chatInLive]);

  const changeRightPanel = (rightPanelView: RightPanelView) => {
    setDisplayControls((settings) => {
      let chatWasHidden = settings.chatWasHidden;
      if (
        rightPanelView === RightPanelView.HIDE_ALL &&
        settings.rightPanel !== RightPanelView.CHAT &&
        !settings.chatWasHidden
      ) {
        rightPanelView = RightPanelView.CHAT;
      }

      if (rightPanelView === RightPanelView.HIDE_ALL && settings.rightPanel === RightPanelView.CHAT) {
        chatWasHidden = true;
      }
      if (rightPanelView === RightPanelView.CHAT) {
        chatWasHidden = false;
      }

      return {
        ...settings,
        rightPanelHidden: rightPanelView === RightPanelView.HIDE_ALL,
        rightPanel: rightPanelView === RightPanelView.HIDE_ALL ? settings.rightPanel : rightPanelView,
        chatWasHidden,
      };
    });
  };

  const setHasUnreadMessage = useCallback(
    (v: boolean) => {
      if (v && !displayControls.rightPanelHidden) return;
      setDisplayControls((settings) => ({
        ...settings,
        hasUnreadMessage: v,
      }));
    },
    [displayControls.rightPanelHidden]
  );

  const onOpenChat = () => {
    setHasUnreadMessage(false);
    changeRightPanel(RightPanelView.CHAT);
  };

  const toggleFullscreen = useCallback(() => {
    setDisplayControls((settings) => ({
      ...settings,
      fullscreen: !settings.fullscreen,
    }));
  }, []);

  const changeControls = (name: keyof DisplayControls, state: boolean) => {
    setDisplayControls((settings) => ({
      ...settings,
      [name]: state,
    }));
  };

  const onShowControls = () => {
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }
    if (displayControls.controls) {
      changeControls("controls", false);
    } else {
      changeControls("controls", true);
      timeout.current = setTimeout(() => {
        if (isMounted.current) changeControls("controls", false);
      }, 3000);
    }
  };

  const onCloseEvent = () => {
    onClose();
    changeControls("confirmationExit", false);
  };

  const onClickEndCall = () => {
    if (showConfirmationExit) changeControls("confirmationExit", true);
    else onCloseEvent();
  };

  useEffect(() => {
    if (isMobile()) return;
    function onEscape(event: KeyboardEvent) {
      if (event.key === "Escape" && displayControls.fullscreen) toggleFullscreen();
    }
    window.addEventListener("keydown", onEscape);
    return () => {
      window.removeEventListener("keydown", onEscape);
    };
  }, [displayControls.fullscreen, toggleFullscreen]);

  return {
    onClickEndCall,
    mobileLandscape,
    onShowControls,
    showControls: displayControls.controls || desktopUp,
    showControlsButtons,
    showStreamPlayButton,
    displayControls,
    onCloseEvent,
    changeControls,
    changeRightPanel,
    desktopUp,
    usersCount,
    onOpenChat,
    setHasUnreadMessage,
    toggleFullscreen,
  };
}

export default useViewController;
